import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, AlertTriangle, RefreshCw, Calendar, CheckCircle, XCircle, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [currentStatus, setCurrentStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [locationFilter, setLocationFilter] = useState('all'); // 'all', 'HQ', 'Home', 'On the Road'
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editTimestamp, setEditTimestamp] = useState('');
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [bottomSectionView, setBottomSectionView] = useState('clocked-in'); // 'clocked-in', 'birthdays', 'bradford-alerts', 'missing-signin', 'on-holiday', 'other-leave', 'off-sick'
  const [showAllRequestsDialog, setShowAllRequestsDialog] = useState(false);
  const [bradfordAlerts, setBradfordAlerts] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [activeTimeOff, setActiveTimeOff] = useState([]);
  const [ongoingSickness, setOngoingSickness] = useState([]);
  const [clockedInTodayIds, setClockedInTodayIds] = useState([]);
  const [showManualSignInDialog, setShowManualSignInDialog] = useState(false);
  const [selectedMissingEmployee, setSelectedMissingEmployee] = useState(null);
  const [manualSignInTime, setManualSignInTime] = useState('');
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);
  const [absenceType, setAbsenceType] = useState('');
  const [absenceEmployee, setAbsenceEmployee] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        window.location.href = '/login';
        return;
      }
      
      const headers = { 'Authorization': `Bearer ${token}` };

      const [statsRes, statusRes, requestsRes, birthdaysRes, alertsRes, employeesRes, timeOffRes, sickRes, clockedTodayRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/timelogs/current-status`, { headers }),
        fetch(`${BACKEND_URL}/api/time-off-requests/pending`, { headers }),
        fetch(`${BACKEND_URL}/api/employees/upcoming-birthdays`, { headers }),
        fetch(`${BACKEND_URL}/api/bradford-alerts`, { headers }),
        fetch(`${BACKEND_URL}/api/employees`, { headers }),
        fetch(`${BACKEND_URL}/api/time-off-requests/active-today`, { headers }),
        fetch(`${BACKEND_URL}/api/sickness/ongoing-all`, { headers }),
        fetch(`${BACKEND_URL}/api/timelogs/clocked-in-today`, { headers })
      ]);

      // Check for authentication errors
      if (statusRes.status === 401 || statusRes.status === 403) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }

      // Handle responses with better error checking
      const statsData = statsRes.ok ? await statsRes.json() : {};
      const statusData = statusRes.ok ? await statusRes.json() : [];
      const requestsData = requestsRes.ok ? await requestsRes.json() : [];
      const birthdaysData = birthdaysRes.ok ? await birthdaysRes.json() : [];
      const alertsData = alertsRes.ok ? await alertsRes.json() : [];
      const employeesData = employeesRes.ok ? await employeesRes.json() : [];
      const timeOffData = timeOffRes.ok ? await timeOffRes.json() : [];
      const sickData = sickRes.ok ? await sickRes.json() : [];
      const clockedTodayData = clockedTodayRes.ok ? await clockedTodayRes.json() : { employee_ids: [] };

      // Data loaded successfully

      setStats(statsData);
      setCurrentStatus(statusData || []);
      setPendingRequests(requestsData || []);
      setUpcomingBirthdays(birthdaysData);
      setBradfordAlerts(alertsData || []);
      setAllEmployees(employeesData || []);
      setActiveTimeOff(timeOffData || []);
      setOngoingSickness(sickData || []);
      setClockedInTodayIds(clockedTodayData.employee_ids || []);
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const detectConflicts = (request) => {
    const requestStart = new Date(request.first_day);
    const requestEnd = new Date(request.last_day);
    
    return pendingRequests.filter(req => {
      if (req.id === request.id) return false; // Skip self
      if (req.status !== 'pending' && req.status !== 'approved') return false;
      if (req.employee_id === request.employee_id) return false; // Skip same employee
      
      const reqStart = new Date(req.first_day);
      const reqEnd = new Date(req.last_day);
      
      // Check if dates overlap
      return (requestStart <= reqEnd && requestEnd >= reqStart);
    });
  };

  // Helper function to check if employee has time off today
  const hasTimeOffToday = (employeeId) => {
    // Check if employee has approved holiday today
    const hasHoliday = activeTimeOff.some(timeOff => timeOff.employee_id === employeeId);
    
    // Check if employee has ongoing sickness/absence today
    const hasSickness = ongoingSickness.some(sick => sick.employee_id === employeeId);
    
    return hasHoliday || hasSickness;
  };

  // Helper function to get time off reason for an employee
  const getTimeOffReason = (employeeId) => {
    const timeOff = activeTimeOff.find(t => t.employee_id === employeeId);
    if (timeOff) return 'Holiday';
    
    const sickness = ongoingSickness.find(s => s.employee_id === employeeId);
    if (sickness) return sickness.absence_type_name || 'Absence';
    
    return null;
  };

  // Calculate missing sign in employees
  const getMissingSignIn = () => {
    const now = new Date();
    const currentTime = now.getTime();
    
    const missing = allEmployees.filter(emp => {
      // CRITICAL: Exclude tech@reuseabox.co.uk (admin-only account, not an employee)
      if (emp.email === 'tech@reuseabox.co.uk') {
        return false;
      }
      
      // Skip archived or inactive employees
      if (emp.is_archived || !emp.active) {
        return false;
      }
      
      // Only check employees in the manager's department
      if (user.role === 'manager' && emp.department !== user.department) {
        return false;
      }
      
      // Skip if employee has time off today (holiday, sickness, absence)
      const hasTimeOff = hasTimeOffToday(emp.id);
      if (hasTimeOff) {
        return false;
      }
      
      // Skip if they have clocked in at any point today (even if they've clocked out)
      if (clockedInTodayIds.includes(emp.id)) {
        return false;
      }
      
      // Check if they should have clocked in (15 minutes past start time)
      if (emp.default_start_time) {
        const [hours, minutes] = emp.default_start_time.split(':');
        const startTime = new Date();
        startTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        const gracePeriod = 15 * 60 * 1000; // 15 minutes in milliseconds
        
        if (currentTime > (startTime.getTime() + gracePeriod)) {
          return true;
        }
      }
      
      return false;
    });
    
    return missing;
  };

  // Calculate employees on holiday
  const getOnHoliday = () => {
    return allEmployees.filter(emp => {
      if (user.role === 'manager' && emp.department !== user.department) {
        return false;
      }
      const reason = getTimeOffReason(emp.id);
      return reason === 'Holiday';
    });
  };

  // Calculate employees on other leave
  const getOtherLeave = () => {
    return allEmployees.filter(emp => {
      if (user.role === 'manager' && emp.department !== user.department) {
        return false;
      }
      const reason = getTimeOffReason(emp.id);
      return reason && reason !== 'Holiday' && reason !== 'Sickness';
    });
  };

  // Calculate employees off sick
  const getOffSick = () => {
    return allEmployees.filter(emp => {
      if (user.role === 'manager' && emp.department !== user.department) {
        return false;
      }
      // Check if employee has ongoing sickness absence
      const hasOngoingSickness = ongoingSickness.some(sick => sick.employee_id === emp.id);
      if (hasOngoingSickness) {
        return true;
      }
      // Also check time-off for sickness holidays
      const reason = getTimeOffReason(emp.id);
      return reason === 'Sickness';
    });
  };

  // Calculate the counts
  const missingSignInEmployees = getMissingSignIn();
  const onHolidayEmployees = getOnHoliday();
  const otherLeaveEmployees = getOtherLeave();
  const offSickEmployees = getOffSick();

  const handleReviewRequest = async (status) => {
    if (!selectedRequest) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/time-off-requests/${selectedRequest.id}/review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: status,
          review_note: reviewNote || undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to review request');
      }

      toast.success(`Request ${status}`);
      setShowReviewDialog(false);
      setSelectedRequest(null);
      setReviewNote('');
      fetchData();
    } catch (error) {
      toast.error('Failed to review request');
    }
  };

  const handleManualClockOff = async (employee) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/timelogs/manual-clock-off`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: employee.employee_id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to clock off employee');
      }

      toast.success(`${employee.first_name} ${employee.last_name} clocked off successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to clock off employee');
    }
  };

  const handleManualSignIn = async () => {
    if (!selectedMissingEmployee || !manualSignInTime) {
      toast.error('Please enter a valid sign-in time');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      const clockInDateTime = `${today}T${manualSignInTime}:00`;

      const response = await fetch(`${BACKEND_URL}/api/timelogs/manual-clock-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: selectedMissingEmployee.id,
          clock_in_time: clockInDateTime,
          location: 'HQ'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to sign in employee');
      }

      toast.success(`${selectedMissingEmployee.first_name} ${selectedMissingEmployee.last_name} signed in successfully`);
      setShowManualSignInDialog(false);
      setSelectedMissingEmployee(null);
      setManualSignInTime('');
      fetchData();
    } catch (error) {
      toast.error('Failed to sign in employee');
    }
  };

  const handleMarkAbsence = async () => {
    if (!absenceEmployee || !absenceType) return;

    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];

      const response = await fetch(`${BACKEND_URL}/api/time-off-requests/manager-book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: absenceEmployee.id,
          absence_type: absenceType,
          first_day: today,
          last_day: today,
          first_day_type: 'full_day',
          last_day_type: 'full_day',
          reason: `Marked as ${absenceType.toLowerCase()} by manager`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to mark absence');
      }

      toast.success(`${absenceEmployee.first_name} ${absenceEmployee.last_name} marked as ${absenceType.toLowerCase()}`);
      setShowAbsenceDialog(false);
      setAbsenceEmployee(null);
      setAbsenceType('');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark absence');
    }
  };

  const handleDismissRequest = async (requestId, e) => {
    e.stopPropagation(); // Prevent opening the review dialog
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/time-off-requests/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to dismiss request');
      }

      toast.success('Request dismissed');
      fetchData();
    } catch (error) {
      toast.error('Failed to dismiss request');
    }
  };

  const handleEditTimestamp = (employee) => {
    setSelectedEmployee(employee);
    setEditTimestamp(employee.clock_in_time);
    setShowEditDialog(true);
  };

  const handleSaveTimestamp = async () => {
    if (!selectedEmployee || !editTimestamp) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/timelogs/edit-timestamp`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: selectedEmployee.employee_id,
          clock_in_time: editTimestamp
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update timestamp');
      }

      toast.success('Timestamp updated successfully');
      setShowEditDialog(false);
      setSelectedEmployee(null);
      setEditTimestamp('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update timestamp');
    }
  };

  const openReviewDialog = (request) => {
    setSelectedRequest(request);
    setShowReviewDialog(true);
    setReviewNote('');
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-teal-600 text-xl">Loading dashboard...</div>
      </div>
    );
  }

  // Filter employees by location
  const getFilteredEmployees = () => {
    if (locationFilter === 'all') return currentStatus;
    // Handle both 'Home' and 'Working from home' for the home filter
    if (locationFilter === 'Working from home' || locationFilter === 'Home') {
      return currentStatus.filter(emp => emp.location === 'Home' || emp.location === 'Working from home' || emp.location === 'Working from Home');
    }
    return currentStatus.filter(emp => emp.location === locationFilter);
  };

  const filteredEmployees = getFilteredEmployees();

  // Count employees by location
  const atHQ = currentStatus.filter(emp => emp.location === 'HQ').length;
  const atHome = currentStatus.filter(emp => emp.location === 'Home' || emp.location === 'Working from home' || emp.location === 'Working from Home').length;
  const onTheRoad = currentStatus.filter(emp => emp.location === 'On the Road').length;

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="dashboard-container">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Department Insights</h2>
            <p className="text-sm text-gray-500">Real-time attendance and team overview</p>
          </div>
          <Button
            data-testid="dashboard-refresh-button"
            onClick={fetchData}
            className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Location Stats - 2 Column on Mobile, 4 Column on Desktop */}
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Current Location</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            onClick={() => setLocationFilter('all')}
            className={`bg-white rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              locationFilter === 'all' ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-emerald-100'
            }`}
            data-testid="total-employees-card"
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold leading-tight">Total Employees</p>
              <div className="w-9 h-9 flex-shrink-0 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800" data-testid="total-employees-count">{currentStatus.length}</p>
          </div>

          <div 
            onClick={() => setLocationFilter('HQ')}
            className={`bg-white rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              locationFilter === 'HQ' ? 'border-teal-500 ring-2 ring-teal-200' : 'border-teal-100'
            }`}
            data-testid="at-hq-card"
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold leading-tight">At HQ</p>
              <div className="w-9 h-9 flex-shrink-0 bg-teal-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-teal-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800" data-testid="at-hq-count">{atHQ}</p>
          </div>

          <div 
            onClick={() => setLocationFilter('Working from home')}
            className={`bg-white rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              locationFilter === 'Working from home' || locationFilter === 'Home' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-blue-100'
            }`}
            data-testid="at-home-card"
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold leading-tight">At Home</p>
              <div className="w-9 h-9 flex-shrink-0 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800" data-testid="at-home-count">{atHome}</p>
          </div>

          <div 
            onClick={() => setLocationFilter('On the Road')}
            className={`bg-white rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              locationFilter === 'On the Road' ? 'border-purple-500 ring-2 ring-purple-200' : 'border-purple-100'
            }`}
            data-testid="on-road-card"
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold leading-tight">On the Road</p>
              <div className="w-9 h-9 flex-shrink-0 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800" data-testid="on-road-count">{onTheRoad}</p>
          </div>
        </div>
      </div>

      {/* Absence Stats - 2 Column on Mobile, 4 Column on Desktop */}
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Absence Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            onClick={() => setBottomSectionView('missing-signin')}
            className={`bg-white rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              bottomSectionView === 'missing-signin' ? 'border-red-500 ring-2 ring-red-200' : 'border-red-100'
            }`}
            data-testid="missing-signin-card"
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold leading-tight">Missing Sign In</p>
              <div className="w-9 h-9 flex-shrink-0 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{missingSignInEmployees.length}</p>
          </div>

          <div 
            onClick={() => setBottomSectionView('on-holiday')}
            className={`bg-white rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              bottomSectionView === 'on-holiday' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-amber-100'
            }`}
            data-testid="on-holiday-card"
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold leading-tight">On Holiday</p>
              <div className="w-9 h-9 flex-shrink-0 bg-amber-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{onHolidayEmployees.length}</p>
          </div>

          <div 
            onClick={() => setBottomSectionView('other-leave')}
            className={`bg-white rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              bottomSectionView === 'other-leave' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-orange-100'
            }`}
            data-testid="other-leave-card"
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold leading-tight">Other Leave</p>
              <div className="w-9 h-9 flex-shrink-0 bg-orange-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{otherLeaveEmployees.length}</p>
          </div>

          <div 
            onClick={() => setBottomSectionView('off-sick')}
            className={`bg-white rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              bottomSectionView === 'off-sick' ? 'border-pink-500 ring-2 ring-pink-200' : 'border-pink-100'
            }`}
            data-testid="off-sick-card"
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-gray-600 text-xs sm:text-sm font-semibold leading-tight">Off Sick</p>
              <div className="w-9 h-9 flex-shrink-0 bg-pink-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-pink-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{offSickEmployees.length}</p>
          </div>
        </div>
      </div>

      {/* Insights - 2 Column on Mobile, 4 Column on Desktop */}
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Team Insights</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            onClick={() => setBottomSectionView('bradford-alerts')}
            className={`bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              bottomSectionView === 'bradford-alerts' ? 'border-red-500 ring-2 ring-red-200' : 'border-red-200'
            }`}
            data-testid="bradford-alerts-card"
          >
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-red-500 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className="text-sm sm:text-lg font-bold text-gray-800 leading-tight">Bradford Alerts</h4>
            </div>
            <p className="text-2xl sm:text-4xl font-bold text-red-700 mb-1 sm:mb-2">{bradfordAlerts.length}</p>
            <p className="text-xs sm:text-sm text-red-600">
              {bradfordAlerts.length === 0 ? 'No alerts' : 'Click to review'}
            </p>
          </div>

          <div 
            onClick={() => setBottomSectionView('birthdays')}
            className={`bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 shadow-md border cursor-pointer transition-all hover:shadow-lg ${
              bottomSectionView === 'birthdays' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-blue-200'
            }`}
            data-testid="upcoming-birthdays-card"
          >
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-blue-500 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className="text-sm sm:text-lg font-bold text-gray-800 leading-tight">Upcoming Birthdays</h4>
            </div>
            <p className="text-2xl sm:text-4xl font-bold text-blue-700 mb-1 sm:mb-2">{upcomingBirthdays.length}</p>
            <p className="text-xs sm:text-sm text-blue-600">In the next 7 days</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 shadow-md border border-purple-200" data-testid="upcoming-time-off-card">
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-purple-500 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className="text-sm sm:text-lg font-bold text-gray-800 leading-tight">Upcoming Time Off</h4>
            </div>
            <p className="text-2xl sm:text-4xl font-bold text-purple-700 mb-1 sm:mb-2">0</p>
            <p className="text-xs sm:text-sm text-purple-600">Scheduled in next 14 days</p>
          </div>

          <div 
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 shadow-md border border-amber-200 cursor-pointer hover:shadow-lg transition-all" 
            data-testid="time-off-requests-card"
            onClick={() => {
              if (pendingRequests.length > 0) {
                setShowAllRequestsDialog(true);
              }
            }}
          >
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-amber-500 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className="text-sm sm:text-lg font-bold text-gray-800 leading-tight">Time Off Requests</h4>
            </div>
            <p className="text-2xl sm:text-4xl font-bold text-amber-700 mb-1 sm:mb-2">{pendingRequests.length}</p>
            <p className="text-xs sm:text-sm text-amber-600">
              {pendingRequests.length === 0 ? 'No pending requests' : 'Click to review'}
            </p>
            {pendingRequests.length > 0 && (
              <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-amber-200">
                <p className="text-xs text-gray-600 truncate">
                  Latest: {pendingRequests[0]?.employee_name}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Currently Clocked In / Upcoming Birthdays / Bradford Alerts */}
      <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="clocked-in-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-emerald-900">
            {bottomSectionView === 'birthdays' ? 'Upcoming Birthdays' : 
             bottomSectionView === 'bradford-alerts' ? 'Bradford Factor Alerts' :
             bottomSectionView === 'missing-signin' ? 'Missing Sign In' :
             bottomSectionView === 'on-holiday' ? 'On Holiday' :
             bottomSectionView === 'other-leave' ? 'Other Leave' :
             bottomSectionView === 'off-sick' ? 'Off Sick' :
             'Currently Clocked In'}
          </h3>
          {bottomSectionView !== 'clocked-in' ? (
            <Button 
              onClick={() => setBottomSectionView('clocked-in')} 
              variant="outline"
              size="sm"
              className="text-xs"
            >
              Show Clocked In
            </Button>
          ) : locationFilter !== 'all' && (
            <Button 
              onClick={() => setLocationFilter('all')} 
              variant="outline"
              size="sm"
              className="text-xs"
            >
              Show All
            </Button>
          )}
        </div>
        
        {bottomSectionView === 'birthdays' ? (
          upcomingBirthdays.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">No birthdays in the next 7 days</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="birthdays-list">
              {upcomingBirthdays.map((person, idx) => (
                <div
                  key={person.employee_id}
                  className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`birthday-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-lg font-bold">
                        {person.first_name[0]}{person.last_name[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-lg text-gray-900">
                          {person.first_name} {person.last_name}
                        </p>
                        <p className="text-sm text-gray-600">{person.department}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-blue-600 font-medium">
                        {person.days_until === 0 ? '🎉 Today!' : 
                         person.days_until === 1 ? 'Tomorrow' : 
                         `In ${person.days_until} days`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(person.birthday_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
                      </p>
                      <p className="text-xs text-gray-500">Turning {person.age_turning}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : bottomSectionView === 'bradford-alerts' ? (
          bradfordAlerts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">No Bradford Factor alerts</p>
              <p className="text-sm mt-2">All employees are below the threshold of 101</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Team Member Name</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Number of Absences</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Number of Days</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Bradford Score</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bradfordAlerts.map((alert, idx) => (
                    <tr 
                      key={alert.employee_id} 
                      className="border-b border-gray-100 hover:bg-red-50 transition-colors"
                      data-testid={`alert-${idx}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">
                            {alert.employee_name.split(' ')[0][0]}{alert.employee_name.split(' ')[1]?.[0] || ''}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{alert.employee_name}</p>
                            <p className="text-xs text-gray-500">{alert.department}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-lg font-semibold text-gray-800">{alert.num_absences}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-lg font-semibold text-gray-800">{alert.total_days}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full font-bold text-lg">
                          {alert.bradford_factor}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Button
                          onClick={() => navigate(`/admin/employees/${alert.employee_id}`)}
                          size="sm"
                          className="bg-teal-600 hover:bg-teal-700 text-white"
                        >
                          View Profile
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : bottomSectionView === 'missing-signin' ? (
          missingSignInEmployees.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 opacity-30 text-green-500" />
              <p className="text-lg">No missing sign-ins</p>
              <p className="text-sm mt-2">All employees have clocked in on time</p>
            </div>
          ) : (
            <div className="space-y-3">
              {missingSignInEmployees.map((emp, idx) => (
                <div
                  key={emp.id}
                  className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {emp.first_name[0]}{emp.last_name[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {emp.first_name} {emp.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{emp.department}</p>
                      <p className="text-xs text-red-600 mt-1">
                        Expected start: {emp.default_start_time || 'Not set'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={() => {
                        setSelectedMissingEmployee(emp);
                        setManualSignInTime('');
                        setShowManualSignInDialog(true);
                      }}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Sign In
                    </Button>
                    <Button
                      onClick={() => {
                        setAbsenceEmployee(emp);
                        setAbsenceType('Other Leave');
                        setShowAbsenceDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                      className="border-orange-500 text-orange-600 hover:bg-orange-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Absent
                    </Button>
                    <Button
                      onClick={() => {
                        setAbsenceEmployee(emp);
                        setAbsenceType('Sickness');
                        setShowAbsenceDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                      className="border-pink-500 text-pink-600 hover:bg-pink-50"
                    >
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Sick
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : bottomSectionView === 'on-holiday' ? (
          onHolidayEmployees.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">No one on holiday today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {onHolidayEmployees.map((emp, idx) => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                      {emp.first_name[0]}{emp.last_name[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {emp.first_name} {emp.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{emp.department}</p>
                      <p className="text-xs text-amber-600 mt-1">On Holiday</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/admin/employees/${emp.id}`)}
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    View Profile
                  </Button>
                </div>
              ))}
            </div>
          )
        ) : bottomSectionView === 'other-leave' ? (
          otherLeaveEmployees.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">No one on other leave today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {otherLeaveEmployees.map((emp, idx) => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                      {emp.first_name[0]}{emp.last_name[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {emp.first_name} {emp.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{emp.department}</p>
                      <p className="text-xs text-orange-600 mt-1">{getTimeOffReason(emp.id)}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/admin/employees/${emp.id}`)}
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    View Profile
                  </Button>
                </div>
              ))}
            </div>
          )
        ) : bottomSectionView === 'off-sick' ? (
          offSickEmployees.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 opacity-30 text-green-500" />
              <p className="text-lg">No one off sick today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {offSickEmployees.map((emp, idx) => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-pink-50 to-red-50 rounded-xl border border-pink-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-red-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                      {emp.first_name[0]}{emp.last_name[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {emp.first_name} {emp.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{emp.department}</p>
                      <p className="text-xs text-pink-600 mt-1">Off Sick</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/admin/employees/${emp.id}`)}
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    View Profile
                  </Button>
                </div>
              ))}
            </div>
          )
        ) : filteredEmployees.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">
              {currentStatus.length === 0 
                ? 'No employees currently clocked in' 
                : `No employees clocked in at ${locationFilter}`}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="clocked-in-list">
            {filteredEmployees.map((emp, idx) => (
              <div
                key={emp.employee_id}
                data-testid={`clocked-in-employee-${idx}`}
                className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-teal-200 hover:shadow-md transition-all"
              >
                {/* Mobile/Tablet Layout - Stack vertically */}
                <div className="flex flex-col gap-4">
                  {/* Top Row: Employee Info */}
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                      {emp.first_name[0]}{emp.last_name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-emerald-900 truncate" data-testid={`employee-name-${idx}`}>
                        {emp.first_name} {emp.last_name}
                      </p>
                      <p className="text-sm text-emerald-600 truncate">{emp.department}</p>
                    </div>
                  </div>

                  {/* Middle Row: Clock Info - 2 column grid on mobile */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Clock In Time</p>
                      <p className="font-semibold text-emerald-900" data-testid={`clock-in-time-${idx}`}>
                        {new Date(emp.clock_in_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Hours Elapsed</p>
                      <p className="font-semibold text-teal-600" data-testid={`elapsed-time-${idx}`}>
                        {emp.elapsed_hours.toFixed(1)}h
                      </p>
                    </div>
                  </div>

                  {/* Location Badge */}
                  <div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-testid={`location-${idx}`}>
                      📍 {emp.location || 'HQ'}
                    </span>
                  </div>

                  {/* Bottom Row: Action Buttons - Full width on mobile */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => handleEditTimestamp(emp)}
                      size="sm"
                      variant="outline"
                      className="w-full text-xs"
                      data-testid={`edit-time-${idx}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit Time
                    </Button>
                    <Button
                      onClick={() => handleManualClockOff(emp)}
                      size="sm"
                      className="w-full bg-red-600 hover:bg-red-700 text-white text-xs"
                      data-testid={`clock-off-${idx}`}
                    >
                      Clock Off
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Review Time Off Request</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Employee Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Employee</p>
                <p className="text-lg font-semibold text-gray-900">{selectedRequest.employee_name}</p>
                <p className="text-sm text-gray-600">{selectedRequest.employee_department}</p>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-blue-600 mb-1">First Day</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(selectedRequest.first_day).toLocaleDateString("en-GB")}
                  </p>
                  <p className="text-xs text-gray-600 mt-1 capitalize">
                    {selectedRequest.first_day_type.replace('_', ' ')}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-xs text-purple-600 mb-1">Last Day</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(selectedRequest.last_day).toLocaleDateString("en-GB")}
                  </p>
                  <p className="text-xs text-gray-600 mt-1 capitalize">
                    {selectedRequest.last_day_type.replace('_', ' ')}
                  </p>
                </div>
              </div>

              {/* Total Days */}
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-4 border border-teal-200">
                <p className="text-sm text-teal-600 mb-1">Total Days Requested</p>
                <p className="text-3xl font-bold text-teal-900">{selectedRequest.total_days} days</p>
              </div>

              {/* Notes */}
              {selectedRequest.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Employee Notes</p>
                  <p className="text-gray-900 italic">"{selectedRequest.notes}"</p>
                </div>
              )}

              {/* Conflicts */}
              {(() => {
                const conflicts = detectConflicts(selectedRequest);
                if (conflicts.length > 0) {
                  return (
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <div className="flex items-start gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-red-900 mb-1">
                            ⚠️ Scheduling Conflicts Detected
                          </p>
                          <p className="text-xs text-red-700 mb-2">
                            {conflicts.length} team member(s) have overlapping time off:
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2 pl-7">
                        {conflicts.map((conflict, idx) => (
                          <div key={idx} className="text-sm text-red-800">
                            • <span className="font-semibold">{conflict.employee_name}</span>: {' '}
                            {new Date(conflict.first_day).toLocaleDateString("en-GB")} - {new Date(conflict.last_day).toLocaleDateString("en-GB")}
                            {' '}({conflict.total_days} days, {conflict.status})
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-sm text-green-900">✓ No scheduling conflicts detected</p>
                    </div>
                  </div>
                );
              })()}

              {/* Manager Review Note */}
              <div>
                <Label htmlFor="review-note">Manager Note (Optional)</Label>
                <Textarea
                  id="review-note"
                  data-testid="review-note-textarea"
                  placeholder="Add any notes about this decision..."
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              data-testid="reject-button"
              onClick={() => handleReviewRequest('rejected')}
              variant="outline"
              className="border-red-500 text-red-600 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button
              data-testid="approve-button"
              onClick={() => handleReviewRequest('approved')}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Time Off Requests Dialog */}
      <Dialog open={showAllRequestsDialog} onOpenChange={setShowAllRequestsDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Time Off Requests ({pendingRequests.length})</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {pendingRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No pending requests
              </div>
            ) : (
              pendingRequests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer relative"
                  onClick={() => {
                    if (request.status !== 'cancelled') {
                      setShowAllRequestsDialog(false);
                      openReviewDialog(request);
                    }
                  }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-lg">{request.employee_name}</p>
                      <p className="text-sm text-gray-600">{request.employee_department}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        request.status === 'approved' ? 'bg-green-100 text-green-700' :
                        request.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {request.status.toUpperCase()}
                      </div>
                      {request.status === 'cancelled' && (
                        <Button
                          onClick={(e) => handleDismissRequest(request.id, e)}
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 hover:bg-red-100"
                        >
                          <XCircle className="w-4 h-4 text-gray-500 hover:text-red-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-blue-50 rounded p-2">
                      <p className="text-xs text-blue-600">First Day</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(request.first_day).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded p-2">
                      <p className="text-xs text-purple-600">Last Day</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(request.last_day).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">{request.total_days}</span> days requested
                    </p>
                    <p className="text-xs text-gray-500">
                      Click to review →
                    </p>
                  </div>
                  
                  {request.notes && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-600 italic line-clamp-2">"{request.notes}"</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Timestamp Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Clock-In Time</DialogTitle>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Employee:</p>
                <p className="font-semibold">{selectedEmployee.first_name} {selectedEmployee.last_name}</p>
              </div>
              
              <div>
                <Label htmlFor="edit-timestamp">Clock-In Time</Label>
                <Input
                  id="edit-timestamp"
                  type="datetime-local"
                  value={editTimestamp ? new Date(editTimestamp).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setEditTimestamp(new Date(e.target.value).toISOString())}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              onClick={() => {
                setShowEditDialog(false);
                setSelectedEmployee(null);
                setEditTimestamp('');
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveTimestamp}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Sign In Dialog */}
      <Dialog open={showManualSignInDialog} onOpenChange={setShowManualSignInDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Sign In</DialogTitle>
          </DialogHeader>
          {selectedMissingEmployee && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">
                  {selectedMissingEmployee.first_name} {selectedMissingEmployee.last_name}
                </p>
                <p className="text-sm text-gray-600">{selectedMissingEmployee.department}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Expected start: {selectedMissingEmployee.default_start_time || 'Not set'}
                </p>
              </div>
              <div>
                <Label htmlFor="signin-time">Actual Arrival Time</Label>
                <Input
                  id="signin-time"
                  type="time"
                  value={manualSignInTime}
                  onChange={(e) => setManualSignInTime(e.target.value)}
                  className="mt-2"
                  placeholder="HH:MM"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowManualSignInDialog(false);
                setSelectedMissingEmployee(null);
                setManualSignInTime('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleManualSignIn}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Sign In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Absence Dialog */}
      <Dialog open={showAbsenceDialog} onOpenChange={setShowAbsenceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as {absenceType}</DialogTitle>
          </DialogHeader>
          {absenceEmployee && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">
                  {absenceEmployee.first_name} {absenceEmployee.last_name}
                </p>
                <p className="text-sm text-gray-600">{absenceEmployee.department}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>This will:</strong>
                </p>
                <ul className="text-sm text-amber-800 mt-2 space-y-1 list-disc list-inside">
                  <li>Mark them as {absenceType.toLowerCase()} for today</li>
                  <li>Update their Bradford Factor</li>
                  <li>Add to company calendar</li>
                  <li>Remove from "Missing Sign In" list</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAbsenceDialog(false);
                setAbsenceEmployee(null);
                setAbsenceType('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkAbsence}
              className={absenceType === 'Sickness' ? 'bg-pink-600 hover:bg-pink-700' : 'bg-orange-600 hover:bg-orange-700'}
            >
              Mark as {absenceType}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
